import { useState, useMemo } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { KeyRound, CheckCircle2, AlertTriangle } from "lucide-react";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const nav = useNavigate();
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [err, setErr] = useState("");

  const missingToken = !token.trim();

  const strength = useMemo(() => {
    if (!password) return null;
    let score = 0;
    if (password.length >= 6) score += 1;
    if (password.length >= 10) score += 1;
    if (/[A-Z]/.test(password) && /[a-z]/.test(password)) score += 1;
    if (/\d/.test(password)) score += 1;
    if (/[^A-Za-z0-9]/.test(password)) score += 1;
    return score;
  }, [password]);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (password.length < 6) { setErr("Password must be at least 6 characters."); return; }
    if (password !== confirm) { setErr("Passwords don't match."); return; }
    setBusy(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: password });
      setDone(true);
      toast.success("Password reset — you can now sign in");
      setTimeout(() => nav("/login"), 1800);
    } catch (e2) {
      setErr(formatApiError(e2.response?.data?.detail) || "Couldn't reset your password. The link may have expired.");
    }
    setBusy(false);
  };

  if (missingToken) {
    return (
      <div className="min-h-[calc(100vh-72px)] grid place-items-center p-6">
        <div className="cp-card p-6 max-w-md text-center" data-testid="reset-invalid">
          <AlertTriangle className="w-10 h-10 text-[#C2185B] mx-auto mb-3" />
          <div className="font-semibold text-[#3B2C24] text-lg">This link is invalid</div>
          <p className="mt-2 text-sm text-[#857163]">The reset link is missing its token. Please request a new one.</p>
          <Link to="/forgot-password" className="mt-6 inline-block cp-btn-primary text-sm" data-testid="request-new-link">
            Request a new link
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-[calc(100vh-72px)] grid place-items-center p-6 sm:p-10">
      <div className="w-full max-w-md" data-testid="reset-password-page">
        <div className="mb-8 text-center">
          <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-[#FF7E67]/15 text-[#FF7E67] mb-4">
            <KeyRound className="w-6 h-6" />
          </div>
          <h1 className="font-heading text-3xl sm:text-4xl font-semibold text-[#3B2C24]">Choose a new password</h1>
          <p className="mt-2 text-sm text-[#857163]">Make it something memorable — but strong. At least 6 characters.</p>
        </div>

        {done ? (
          <div className="cp-card p-6 text-center" data-testid="reset-done">
            <CheckCircle2 className="w-10 h-10 text-[#8EAC50] mx-auto mb-3" />
            <div className="font-semibold text-[#3B2C24] text-lg">Password reset</div>
            <p className="mt-2 text-sm text-[#857163]">Taking you to the login page…</p>
          </div>
        ) : (
          <form onSubmit={submit} className="cp-card p-6 space-y-4">
            <label className="block">
              <span className="text-sm font-medium text-[#3B2C24]">New password</span>
              <input
                type="password"
                className="cp-input mt-1"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                minLength={6}
                autoFocus
                data-testid="reset-password-new"
              />
              {strength != null && (
                <div className="mt-2 flex gap-1">
                  {[1, 2, 3, 4, 5].map((n) => (
                    <div
                      key={n}
                      className="h-1 flex-1 rounded-full transition"
                      style={{
                        background: n <= strength
                          ? (strength <= 2 ? "#C2185B" : strength <= 3 ? "#F4A83B" : "#8EAC50")
                          : "#EBDCD1",
                      }}
                    />
                  ))}
                </div>
              )}
            </label>

            <label className="block">
              <span className="text-sm font-medium text-[#3B2C24]">Confirm new password</span>
              <input
                type="password"
                className="cp-input mt-1"
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                required
                minLength={6}
                data-testid="reset-password-confirm"
              />
            </label>

            {err && <div className="text-sm text-red-600" data-testid="reset-password-error">{err}</div>}

            <button
              type="submit"
              disabled={busy || !password || !confirm}
              className="cp-btn-primary w-full disabled:opacity-70"
              data-testid="reset-password-submit"
            >
              {busy ? "Resetting…" : "Reset password"}
            </button>
            <div className="text-center">
              <Link to="/login" className="text-xs text-[#857163] hover:text-[#3B2C24]" data-testid="back-to-login">
                &larr; Back to sign in
              </Link>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
